import {
  Button, Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  makeStyles, MenuItem, Select,
  Slide, Switch,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import {TransitionProps} from '@material-ui/core/transitions/transition';
import React, {useEffect, useRef, useState} from 'react';
import * as yup from 'yup';
import {Formik, FormikProps} from 'formik';
import Cube, {DifficultyType, GameType, JourneyPhase, LengthOfGame, NumberOfPlayers} from '../../../Domain/Models/Cube';
import {cubeRepository} from "../../../Domain/Repositories/CubeRepository";
import Loading from "../../UiComponents/Loading";
import {CheckBox} from "@material-ui/icons";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  form: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  dialog: {
    alignItems: 'flex-start',
  },
  deleteFlex: {
    flex: 1,
  }
}));

const defaultCube: Cube = {
  id: '',
  name: '',
  averagePlayTimeSeconds: 0,
  isBulletTime: false,
  difficultyTypes: [ DifficultyType.Band1 ],
  gameType: GameType.Floor,
  journeyPhases: [ JourneyPhase.Start ],
  lengthOfGame: LengthOfGame.Quick,
  numberOfPlayers: NumberOfPlayers.One,
  maintenanceMode: false
};

export interface CubeModalProps
{
  open: boolean;
  onClose: () => void;
  cube?: Cube;
}

export default function CubeModal(props: CubeModalProps)
{
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {open, onClose} = props;
  const classes = useStyles();

  const [cube, setCube] = useState<Cube>(defaultCube);
  const [updateMode, setUpdateMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  const formRef = useRef<FormikProps<Cube>>(null);

  const schema = yup.object().shape({
    name: yup.string().required('Required'),
    averagePlayTimeSeconds: yup.number().positive('Must be positive'),
    difficultyTypes: yup.array().required('Required').min(1, 'Required'),
    journeyPhases: yup.array().required('Required').min(1, 'Required'),
  });

  const handleSubmit = (value: Cube) =>
  {
    setSubmitting(true);
    setSubmitError(undefined);

    if (updateMode)
    {
      cubeRepository.update(value)
          .then(() => onClose())
          .catch((e: Error) =>
          {
            setSubmitting(false);
            setSubmitError(e.message);
          });
    }
    else
    {
      cubeRepository.add(value)
          .then(() => onClose())
          .catch((e: Error) =>
          {
            setSubmitting(false);
            setSubmitError(e.message);
          });
    }
  }

  useEffect(() =>
  {
    if (open)
    {
      setSubmitError(undefined);
      setSubmitting(false);

      if (props.cube !== undefined)
      {
        setCube(props.cube);
        setUpdateMode(true);
      }
      else
      {
        setCube(defaultCube);
        setUpdateMode(false);
      }
    }
  }, [open]);

  return (
      <Dialog
          classes={{scrollPaper: classes.dialog}}
          open={open}
          onClose={onClose}
          fullScreen={fullScreen}
          fullWidth
          maxWidth="md"
          disableEscapeKeyDown
          disableBackdropClick={submitting}
          TransitionComponent={Transition}
      >
        <DialogTitle>Cube</DialogTitle>

        <Loading isLoading={submitting}>
          <DialogContent>
            <Formik<Cube>
                initialValues={cube}
                onSubmit={(value) => handleSubmit(value)}
                validationSchema={schema}
                innerRef={formRef}
            >
              {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur
                }) => (
                  <>
                    <FormControl fullWidth margin="normal" error={touched.name && errors.name !== undefined}>
                      <InputLabel htmlFor="component-error">Name</InputLabel>
                      <Input
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}/>
                      <FormHelperText>{touched.name && errors.name}</FormHelperText>
                    </FormControl>
                    
                    <FormControl fullWidth margin="normal" error={touched.averagePlayTimeSeconds && errors.averagePlayTimeSeconds !== undefined}>
                      <InputLabel htmlFor="component-error">Average Play Time (Seconds)</InputLabel>
                      <Input
                          name="averagePlayTimeSeconds"
                          type="number"
                          value={values.averagePlayTimeSeconds}
                          onChange={handleChange}
                          onBlur={handleBlur}/>
                      <FormHelperText>{touched.averagePlayTimeSeconds && errors.averagePlayTimeSeconds}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={touched.gameType && errors.gameType !== undefined}>
                      <InputLabel htmlFor="component-error">Game Type</InputLabel>
                      <Select
                          name="gameType"
                          value={values.gameType}
                          onChange={handleChange}
                      >
                        {Object.keys(GameType).map((value) => <MenuItem value={value}>{value}</MenuItem>)}
                      </Select>
                      <FormHelperText>{touched.gameType && errors.gameType}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={touched.numberOfPlayers && errors.numberOfPlayers !== undefined}>
                      <InputLabel htmlFor="component-error">Number Of Players</InputLabel>
                      <Select
                          name="numberOfPlayers"
                          value={values.numberOfPlayers}
                          onChange={handleChange}
                      >
                        {Object.keys(NumberOfPlayers).map((value) => <MenuItem value={value}>{value}</MenuItem>)}
                      </Select>
                      <FormHelperText>{touched.numberOfPlayers && errors.numberOfPlayers}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={touched.journeyPhases && errors.journeyPhases !== undefined}>
                      <InputLabel htmlFor="component-error">Journey Phases</InputLabel>
                      <Select
                          name="journeyPhases"
                          value={values.journeyPhases}
                          onChange={handleChange}
                          multiple
                      >
                        {Object.keys(JourneyPhase).map((value) => <MenuItem value={value}>{value}</MenuItem>)}
                      </Select>
                      <FormHelperText>{touched.journeyPhases && errors.journeyPhases}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={touched.difficultyTypes && errors.difficultyTypes !== undefined}>
                      <InputLabel htmlFor="component-error">Difficulty</InputLabel>
                      <Select
                          name="difficultyTypes"
                          value={values.difficultyTypes}
                          onChange={handleChange}
                          multiple
                      >
                        {Object.keys(DifficultyType).map((value) => <MenuItem value={value}>{value}</MenuItem>)}
                      </Select>
                      <FormHelperText>{touched.difficultyTypes && errors.difficultyTypes}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={touched.lengthOfGame && errors.lengthOfGame !== undefined}>
                      <InputLabel htmlFor="component-error">Length</InputLabel>
                      <Select
                          name="lengthOfGame"
                          value={values.lengthOfGame}
                          onChange={handleChange}
                      >
                        {Object.keys(LengthOfGame).map((value) => <MenuItem value={value}>{value}</MenuItem>)}
                      </Select>
                      <FormHelperText>{touched.lengthOfGame && errors.lengthOfGame}</FormHelperText>
                    </FormControl>

                    <FormControl fullWidth margin="normal" error={touched.isBulletTime && errors.isBulletTime !== undefined}>
                      <InputLabel htmlFor="component-error">Bullet Time</InputLabel>
                      <Checkbox
                        name="isBulletTime"
                        checked={values.isBulletTime}
                        onChange={handleChange}
                        color="primary"
                      />
                      <FormHelperText>{touched.isBulletTime && errors.isBulletTime}</FormHelperText>
                    </FormControl>
                  </>
              )}
            </Formik>

            {submitError != undefined && (
                <Typography component="h3" variant="subtitle1" color="error" gutterBottom>
                  {submitError}
                </Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button
                onClick={onClose} color="primary"
            >
              Cancel
            </Button>
            <Button
                onClick={() => formRef.current?.handleSubmit()}
                color="primary"
            >
              Submit
            </Button>
          </DialogActions>

        </Loading>
      </Dialog>
  );
}